import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { brandService, masterService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";
import BarcodeScanInput from "../../../components/BarcodeScanInput";
import { formatNumberWithSeparator, parseNumberFromSeparator } from "../../../utils/numberFormat";

export default function EditVariantModal({ isOpen, onClose, variant, productId, product, onSuccess, isBrandVariant, isRetail }) {
  const { showSuccess, showError } = useNotification();
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  const isRetailMode = isRetail !== undefined ? isRetail : !isApps;
  const isEditing = !!variant;

  const [activeTab, setActiveTab] = useState("product"); // "product" | "brand"
  const [formData, setFormData] = useState({
    name: variant?.name || "",
    sku: variant?.sku || "",
    barcode: variant?.barcode || "",
    purchase_price: variant?.purchase_price !== undefined ? variant.purchase_price : (product?.purchase_price || product?.buyPrice || 0),
    sell_price: variant?.sell_price || 0,
    unit_id: variant?.unit_id || "",
    brand_id: variant?.brand_id || "",
    prep_time_mins: variant?.prep_time_mins || 0
  });
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const isBrand = !!variant?.brand_id || !!isBrandVariant;
      setActiveTab(isBrand ? "brand" : "product");

      const defaultPurchasePrice = variant?.purchase_price !== undefined && variant?.purchase_price !== null
        ? variant.purchase_price
        : (product?.purchase_price || product?.buyPrice || 0);

      setFormData({
        name: variant?.name || "",
        sku: variant?.sku || "",
        barcode: variant?.barcode || "",
        purchase_price: defaultPurchasePrice,
        sell_price: variant?.sell_price || 0,
        unit_id: variant?.unit_id || "",
        brand_id: variant?.brand_id || "",
        prep_time_mins: variant?.prep_time_mins || 0
      });

      fetchBrands();
    }
  }, [isOpen, variant, isBrandVariant, product]);

  const fetchBrands = async () => {
    try {
      const res = await brandService.list();
      setBrands(res.data?.data || res.data || []);
    } catch (error) {
      console.error("Failed to fetch brands", error);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBrandChange = (e) => {
    const selectedBrandId = e.target.value;
    const selectedBrandObj = brands.find(b => String(b.id) === String(selectedBrandId));
    setFormData(prev => ({
      ...prev,
      brand_id: selectedBrandId,
      name: selectedBrandObj ? selectedBrandObj.name : ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isBrandType = activeTab === "brand";

      const payload = {
        ...formData,
        purchase_price: parseNumberFromSeparator(formData.purchase_price),
        sell_price: parseNumberFromSeparator(formData.sell_price),
        prep_time_mins: Number(formData.prep_time_mins || 0)
      };

      if (isBrandType) {
        if (!payload.brand_id) {
          showError("Silakan pilih Merek");
          setLoading(false);
          return;
        }
        // Name can be blank or set to brand name
        const bObj = brands.find(b => String(b.id) === String(payload.brand_id));
        if (bObj && !payload.name) payload.name = bObj.name;
      } else {
        payload.brand_id = null;
      }

      if (!payload.unit_id) payload.unit_id = null;

      if (isEditing) {
        await masterService.products.variants.update(productId, variant.id, payload);
        showSuccess("Varian berhasil diperbarui");
      } else {
        await masterService.products.variants.create(productId, payload);
        showSuccess("Varian berhasil ditambahkan");
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      showError(error.message || "Gagal menyimpan varian");
    } finally {
      setLoading(false);
    }
  };

  const isBrandTab = activeTab === "brand";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 flex justify-between items-center bg-teal-600 text-white rounded-t-2xl">
          <h2 className="text-base font-semibold tracking-tight">
            {isEditing 
              ? (isBrandTab ? "Edit Varian Merek" : "Edit Varian Produk") 
              : (isRetailMode ? "Tambah Varian (Produk / Merek)" : "Tambah Varian Produk")}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher for Retail mode (when creating new variant) */}
        {isRetailMode && !isEditing && (
          <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-3 gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("product")}
              className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "product"
                  ? "border-teal-600 text-teal-700 bg-white rounded-t-lg shadow-sm"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              Varian Produk
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("brand")}
              className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "brand"
                  ? "border-teal-600 text-teal-700 bg-white rounded-t-lg shadow-sm"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              Varian Merek
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
          {isBrandTab ? (
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Merek</label>
              <select 
                name="brand_id" 
                value={formData.brand_id} 
                onChange={handleBrandChange} 
                required 
                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 bg-white"
              >
                <option value="">Pilih Merek</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Nama Varian</label>
              <input 
                type="text" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                required 
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" 
                placeholder="Misal: Warna Merah / Ukuran XL / Pedas" 
              />
            </div>
          )}

          {/* SKU & Barcode for Retail */}
          {isRetailMode && (
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-700">SKU Varian</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-700">Barcode Varian</label>
                <BarcodeScanInput
                  value={formData.barcode}
                  onChange={(val) => setFormData((prev) => ({ ...prev, barcode: val }))}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-colors"
                  focusClass="focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                />
              </div>
            </div>
          )}

          {/* HIDDEN TEMPORARILY: Estimasi Waktu (Menit)
          <div className="space-y-1">
            <label className="text-sm font-semibold text-slate-700">Estimasi Waktu (Menit)</label>
            <input type="number" name="prep_time_mins" value={formData.prep_time_mins} onChange={handleChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500" placeholder="Misal: 15" />
          </div>
          */}

          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Harga Modal</label>
              <input
                type="text"
                inputMode="numeric"
                name="purchase_price"
                value={formatNumberWithSeparator(formData.purchase_price)}
                onChange={(e) => setFormData(prev => ({ ...prev, purchase_price: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Harga Jual</label>
              <input
                type="text"
                inputMode="numeric"
                name="sell_price"
                value={formatNumberWithSeparator(formData.sell_price)}
                onChange={(e) => setFormData(prev => ({ ...prev, sell_price: e.target.value }))}
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors">Batal</button>
            <button type="submit" disabled={loading} className="px-5 py-2 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50">
              {loading ? "Menyimpan..." : "Simpan Varian"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
